/**
 * Application Insights initialization (module-level execution)
 * Runs BEFORE React components are rendered
 *
 * Handles:
 * - Initializing AppInsights SDK
 * - Detecting PWA mode (standalone vs browser)
 * - Tracking initial app state
 */

import { initializeAppInsights as initAppInsights, trackEvent } from '../services/AppInsightsPlugin';
import { isSWAvailable, isProduction } from '../config';
import { Logger } from '../utils/Logger';

/**
 * Extend Navigator interface to include iOS Safari's non-standard 'standalone' property
 */
declare global {
    interface Navigator {
        standalone?: boolean;
    }
}

let appInsights: ReturnType<typeof initAppInsights> | null = null;

/**
 * Initialize Application Insights with PWA detection
 *
 * Tracks:
 * - APP_INIT event with SW availability and display mode
 * - PWA installation status
 */
export function initializeAppInsights(): ReturnType<typeof initAppInsights> {
  appInsights = initAppInsights();

  if (appInsights) {
    // ============================================================================
    // PII Filtering & Data Sanitization
    // ============================================================================
    // Telemetry initializer to filter PII and exclude unnecessary routes
    appInsights.addTelemetryInitializer((envelope) => {
      // 1. Filter out admin/health check routes (if they exist)
      if (envelope.baseType === 'PageviewData') {
        const url = envelope.baseData?.uri;
        if (url?.includes('/_admin') || url?.includes('/health')) {
          return false; // Don't send
        }
      }

      // 2. Remove PII from exceptions and custom events
      if (envelope.baseType === 'ExceptionData' || envelope.baseType === 'EventData') {
        const customProps = envelope.data?.customProperties;
        if (customProps) {
          // Remove personally identifiable information
          delete customProps.email;
          delete customProps.phoneNumber;
          delete customProps.address;
          delete customProps.nom;
          delete customProps.prenom;
          delete customProps.password;
          delete customProps.token;
        }
      }

      // 3. Add environment tag for better filtering in dashboards
      envelope.data = envelope.data || {};
      (envelope.data as any).environment = isProduction ? 'production' : 'development';

      return true; // Send
    });

    Logger.info('AppInsights', '✅ Telemetry processor configured with PII filtering');

    // Detect PWA mode (standalone vs browser)
    const displayMode = window.matchMedia('(display-mode: standalone)').matches
      ? 'standalone'
      : window.matchMedia('(display-mode: window-controls-overlay)').matches
        ? 'window-controls-overlay'
        : 'browser';

    const isPWAInstalled = displayMode === 'standalone' || navigator.standalone === true;

    Logger.debug('APP', 'STARTUP: Tracking APP_INIT event');
    trackEvent('APP_INIT', {
      swAvailable: isSWAvailable,
      displayMode: displayMode,
      isPWAInstalled: isPWAInstalled
    });
  }

  return appInsights;
}

/**
 * Get the AppInsights instance
 */
export function getAppInsights(): ReturnType<typeof initAppInsights> | null {
  return appInsights;
}
